import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import TcnApp from '../../../components/tcn/TcnApp';
import TopBar from '../../../components/tcn/layout/TopBar';
import { Card, AccountAvatar, Icon, CTAButton, Chip } from '../../../components/tcn/ui-kit';
import { TCN } from '../../../components/tcn/ui-kit/tokens';
import AlertRow from '../../../components/tcn-modules/m7/AlertRow';
import NotificationSettings from '../../../components/tcn-modules/m7/NotificationSettings';
import { ALERT_TYPES, ALERT_TYPE_BY_ID } from '../../../components/tcn-modules/m7/types';
import useUnreadAlerts from '../../../components/tcn-modules/m7/useUnreadAlerts';
import useIsDesktop from '../../../hooks/useIsDesktop';

/**
 * /tcn/me/alerts — Module 7 — Alerts & notifications (in-app).
 *
 * RLS strict: agent_id = auth.uid().
 *
 * - Lists all alerts for the agent, grouped by type with a status filter
 *   (all / unread / read). Newest first.
 * - Tap a row → marks read_at = now() and deep-links to the relevant entity
 *   (creator detail, payout page, new-creators tracker…).
 * - "Mark all as read" CTA at the top when unread > 0.
 * - Tab bar context: not a primary tab — accessible via the bell icon in any
 *   manager TopBar trailing slot. We hide the tab bar for this page so the
 *   page feels like a notification center sheet.
 *
 * Phase 2 (Task #12): swap the in-app refresh-driven count for a real push
 * channel via VAPID + service workers + quiet hours.
 */
const TcnAlertsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const isDesktop = useIsDesktop();
  const [searchParams, setSearchParams] = useSearchParams();
  const isSettings = searchParams.get('tab') === 'settings';
  const openSettings = () => setSearchParams({ tab: 'settings' }, { replace: false });
  const closeSettings = () => setSearchParams({}, { replace: false });
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('unread'); // 'all' | 'unread' | 'read'
  const [typeFilter, setTypeFilter] = useState('all');
  const { refresh: refreshUnread } = useUnreadAlerts(user?.id);

  const userName = userProfile?.full_name || userProfile?.display_name || userProfile?.email || 'Agent';

  const load = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    setError(null);
    const { data, error: err } = await supabase
      .from('alert_events')
      .select('*')
      .eq('agent_id', user.id)
      .order('created_at', { ascending: false })
      .limit(200);
    if (err) setError(err);
    else setAlerts(data || []);
    setLoading(false);
  }, [user?.id]);

  useEffect(() => { load(); }, [load]);

  const handleTabChange = (tab) => {
    switch (tab) {
      case 'today':    return navigate('/tcn/me');
      case 'creators': return navigate('/tcn/me/creators');
      case 'tiers':    return navigate('/tcn/me/tiers');
      case 'payout':   return navigate('/tcn/me/payout');
      case 'actions':  return navigate('/tcn/me/actions');
      default:         return navigate('/tcn/me');
    }
  };

  const counts = useMemo(() => {
    const c = { all: alerts.length, unread: 0, read: 0 };
    for (const a of alerts) {
      if (a.read_at) c.read += 1;
      else c.unread += 1;
    }
    return c;
  }, [alerts]);

  const visible = useMemo(() => {
    return alerts.filter((a) => {
      if (filter === 'unread' && a.read_at) return false;
      if (filter === 'read' && !a.read_at) return false;
      if (typeFilter !== 'all' && a.type !== typeFilter) return false;
      return true;
    });
  }, [alerts, filter, typeFilter]);

  const markRead = async (alert) => {
    if (alert.read_at) return;
    const now = new Date().toISOString();
    setAlerts((prev) => prev.map(a => a.id === alert.id ? { ...a, read_at: now } : a));
    await supabase
      .from('alert_events')
      .update({ read_at: now })
      .eq('id', alert.id);
    refreshUnread();
  };

  const handleActivate = async (alert) => {
    const meta = ALERT_TYPE_BY_ID[alert.type] ?? ALERT_TYPE_BY_ID.other;
    const target = meta.deepLink?.(alert.payload_json || {}) ?? null;
    // Mark read fire-and-forget (UI update is optimistic).
    markRead(alert);
    if (target) navigate(target);
  };

  const markAllRead = async () => {
    if (!user?.id || counts.unread === 0) return;
    const now = new Date().toISOString();
    setAlerts((prev) => prev.map(a => a.read_at ? a : { ...a, read_at: now }));
    await supabase
      .from('alert_events')
      .update({ read_at: now })
      .eq('agent_id', user.id)
      .is('read_at', null);
    refreshUnread();
  };

  return (
    <TcnApp
      tab={null}
      onTabChange={handleTabChange}
      desktopUserName={userName}
      desktopBreadcrumb={[
        t('tcn.m1.breadcrumb.dashboard', 'Dashboard'),
        t('tcn.m7.title', 'Alerts'),
      ]}
      mobileHeader={
        <TopBar
          leading={
            <button
              type="button"
              onClick={() => (isSettings ? closeSettings() : navigate(-1))}
              aria-label={t('tcn.common.back', 'Back')}
              style={{ background: 'transparent', border: 'none', color: TCN.cyan, cursor: 'pointer', padding: 4 }}
            >
              <Icon name="chevron-left" size={20} />
            </button>
          }
          title={isSettings ? t('tcn.m7.settings.title', 'Notification settings') : t('tcn.m7.title', 'Alerts')}
          subtitle={isSettings ? undefined : t('tcn.m7.subtitle', '{{count}} unread', { count: counts.unread })}
          largeTitle
          trailing={
            isSettings ? null : (
              <button
                type="button"
                onClick={openSettings}
                aria-label={t('tcn.m7.settings.open', 'Notification settings')}
                style={{ background: 'transparent', border: 'none', color: TCN.textDim, cursor: 'pointer', padding: 4 }}
              >
                <Icon name="settings" size={20} />
              </button>
            )
          }
        />
      }
    >
      <div
        style={{
          maxWidth: isDesktop ? 1000 : '100%',
          padding: isDesktop ? 0 : '0 16px 16px',
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        {isSettings ? (
          <NotificationSettings userId={user?.id} />
        ) : (
        <>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 8,
          }}
        >
          {[
            { id: 'unread', label: t('tcn.m7.filter.unread', 'Unread'), color: 'cyan',     count: counts.unread },
            { id: 'all',    label: t('tcn.m7.filter.all', 'All'),       color: 'cyan',     count: counts.all },
            { id: 'read',   label: t('tcn.m7.filter.read', 'Read'),     color: 'cyan',     count: counts.read },
          ].map((s) => {
            const active = filter === s.id;
            return (
              <Chip
                key={s.id}
                color={s.color}
                solid={active}
                size="md"
                onClick={() => setFilter(s.id)}
                trailing={
                  <span style={{ fontSize: 10, opacity: 0.7, fontWeight: 700 }}>{s.count}</span>
                }
              >
                {s.label}
              </Chip>
            );
          })}

          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            style={{
              marginInlineStart: 'auto',
              background: TCN.surface2,
              color: TCN.text,
              border: `1px solid ${TCN.border}`,
              padding: '6px 10px',
              borderRadius: 8,
              // FRM-001/TCH-001 — 16px : pas de zoom iOS à l'ouverture du select.
              fontSize: 16,
              fontFamily: TCN.fontSans,
              cursor: 'pointer',
            }}
          >
            <option value="all">{t('tcn.m7.filter.allTypes', 'All types')}</option>
            {ALERT_TYPES.map((tp) => (
              <option key={tp.id} value={tp.id}>
                {t(`tcn.m7.type.${tp.id}`, tp.label)}
              </option>
            ))}
          </select>

          {counts.unread > 0 && (
            <CTAButton variant="secondary" size="sm" onClick={markAllRead} fullWidth={false}>
              {t('tcn.m7.markAllRead', 'Mark all read')}
            </CTAButton>
          )}
        </div>

        {loading && (
          <Card padded style={{ padding: 16, color: TCN.textDim, fontSize: 16 }}>
            {t('tcn.common.loading', 'Loading…')}
          </Card>
        )}
        {error && (
          <Card padded style={{ padding: 16, color: TCN.red, fontSize: 16 }}>
            {error.message}
          </Card>
        )}

        {!loading && !error && visible.length === 0 && (
          <Card padded style={{ padding: 24, color: TCN.textDim, fontSize: 16, textAlign: 'center' }}>
            {filter === 'unread' && counts.all > 0
              ? t('tcn.m7.empty.allRead', 'You\'re all caught up.')
              : t('tcn.m7.empty.none', 'No alerts in this view.')}
          </Card>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {visible.map((a) => (
            <AlertRow key={a.id} alert={a} onActivate={handleActivate} />
          ))}
        </div>
        </>
        )}
      </div>
    </TcnApp>
  );
};

export default TcnAlertsPage;
